import React, { useState, useEffect, useCallback } from "react";
import { makeAuthenticatedRequest } from "../../utils/apiRequest";
import { useAuth } from "../../contexts/authContext";

// Update the component to accept days prop
const MiscellaneousExpensesPopup = ({ isOpen, onClose, itineraryId, days = [] }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
// Track currency per expense (default: $)
const [currencyMap, setCurrencyMap] = useState({});
// Store USD rate for conversion
const [usdRate, setUsdRate] = useState(1); // fallback to 1 if not found
// Store tax percentage for use in calculation
const getTaxPercentage = () => {
  const itineraryData = apiResponseData?.data?.[0] || apiResponseData;
  if (itineraryData && itineraryData.taxes && itineraryData.taxes.length > 0) {
    return itineraryData.taxes[0].tax_percentage || 0;
  }
  return 0;
};
  const [apiResponseData, setApiResponseData] = useState(null);

  // Sample data based on provided UI
  const getSampleData = () => {
    return [
      {
        day: "01",
        date: "(20XX-XX-XX)",
        expenses: [
          { type: "Pack lunch", amount: 100, taxIncluded: false },
          { type: "Water", amount: 50, taxIncluded: false }
        ]
      },
      {
        day: "02",
        date: "(20XX-XX-XX)",
        expenses: [
          { type: "Pack lunch", amount: 120, taxIncluded: false },
          { type: "Water", amount: 60, taxIncluded: false }
        ]
      }
    ];
  };

  // Fetch miscellaneous expenses from API (structure similar to OtherExpensesPopup)
  const fetchMiscellaneousExpenses = useCallback(async () => {
    if (!token || !itineraryId) {
      setExpenses(getSampleData());
      return;
    }
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ itinerary_id: itineraryId }),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const responseData = await response.json();
      setApiResponseData(responseData);
      // Extract and format miscellaneous expenses from itinerary_cost
      const itineraryData = responseData.data && responseData.data[0] ? responseData.data[0] : responseData;

      // --- Extract USD rate from rates array in itinerary JSON ---
      if (Array.isArray(itineraryData.rates)) {
        const usdObj = itineraryData.rates.find(c => c.currency_name && c.currency_name.includes("USD"));
        if (usdObj && usdObj.value) {
          setUsdRate(Number(usdObj.value));
        } else {
          setUsdRate(1); // fallback if not found
        }
      } else {
        setUsdRate(1); // fallback if rates array missing
      }

      if (itineraryData.itinerary_cost && Array.isArray(itineraryData.itinerary_cost)) {
        const formattedData = [];
        const newCurrencyMap = {};
        itineraryData.itinerary_cost.forEach((dayData, dayIndex) => {
          // Use sample labels if not present
          const miscExpenses = (dayData.miscellaneous_expenses && Array.isArray(dayData.miscellaneous_expenses))
            ? dayData.miscellaneous_expenses
            : [
                { type: "Pack lunch", amount: 0 },
                { type: "Water", amount: 0 }
              ];
          // Set currencyMap for each expense
          miscExpenses.forEach((e, expenseIndex) => {
            if (e.currency === 'RS') {
              newCurrencyMap[`${formattedData.length}-${expenseIndex}`] = 'RS';
            } else {
              newCurrencyMap[`${formattedData.length}-${expenseIndex}`] = '$';
            }
          });
          formattedData.push({
            day: dayData.day?.replace('Day ', '') || "--",
            date: dayData.date || "(----/--/--)",
            expenses: miscExpenses.map(e => {
  const currency = e.currency || '$';
  const amountUSD = typeof e.amount === "number" ? e.amount : 0;
  let enteredRS;
  if (currency === 'RS') {
    enteredRS = (amountUSD * usdRate).toFixed(2);
  }
  return {
    type: e.type || "Expense",
    baseAmount: typeof e.baseAmount === "number"
      ? e.baseAmount
      : amountUSD,
    amount: amountUSD,
    taxIncluded: typeof e.taxIncluded === "boolean" ? e.taxIncluded : false,
    enteredRS,
  };
})
          });
        });
        
        // Filter out expenses for days that have been removed
        if (days && days.length > 0) {
          // Get the current day numbers from the days array
          const currentDayNumbers = days.map(day => day.number.toString().padStart(2, '0'));
          console.log('MiscellaneousExpensesPopup: Current day numbers:', currentDayNumbers);
          
          // Filter the formatted data to only include existing days
          const filteredData = formattedData.filter(item => {
            return currentDayNumbers.includes(item.day);
          });
          
          console.log('MiscellaneousExpensesPopup: Filtered data after removing deleted days:', filteredData);
          setExpenses(filteredData);
          setCurrencyMap(newCurrencyMap);
        } else {
          setExpenses(formattedData);
          setCurrencyMap(newCurrencyMap);
        }
      } else {
        setExpenses(getSampleData());
      }
    } catch (err) {
      setError(err.message);
      setExpenses(getSampleData());
    } finally {
      setLoading(false);
    }
  }, [token, itineraryId, days]);

  // Fetch miscellaneous expenses when popup opens
useEffect(() => {
  if (isOpen) {
    fetchMiscellaneousExpenses();
  }
}, [isOpen, fetchMiscellaneousExpenses]);

  // Format numbers to 2 decimal places with safety check
  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return parseFloat(value).toFixed(2);
  };

  // Handle currency toggle with value conversion
  const handleCurrencyToggle = (dayIndex, expenseIndex) => {
    setCurrencyMap((prev) => {
      const key = `${dayIndex}-${expenseIndex}`;
      const current = prev[key] || '$';
      const updatedExpenses = [...expenses];
      const expense = updatedExpenses[dayIndex]?.expenses[expenseIndex];
      if (expense) {
        const taxPercentage = getTaxPercentage();
        if (current === '$') {
          // $ → Rs: convert USD value to Rs and update input
          const usdVal = parseFloat(expense.amount) || 0;
          const rsVal = (usdVal * usdRate).toFixed(2);
          expense.enteredRS = rsVal;
          
          // Preserve tax calculation when switching to RS
          if (expense.taxIncluded) {
            // If tax is included, we need to ensure baseAmount is correct
            const baseAmount = usdVal / (1 + taxPercentage / 100);
            expense.baseAmount = baseAmount;
            expense.taxAmount = (usdVal - baseAmount).toFixed(2);
          }
        } else {
          // Rs → $: convert Rs value to USD and update input
          const rsVal = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) || 0 : parseFloat(expense.amount) || 0;
          const usdVal = (rsVal / usdRate).toFixed(2);
          expense.amount = usdVal;
          delete expense.enteredRS;
          
          // Preserve tax calculation when switching back to USD
          if (expense.taxIncluded) {
            // If tax is included, we need to ensure baseAmount is correct
            const baseAmount = parseFloat(usdVal) / (1 + taxPercentage / 100);
            expense.baseAmount = baseAmount;
            expense.taxAmount = (parseFloat(usdVal) - baseAmount).toFixed(2);
          } else {
            expense.baseAmount = parseFloat(usdVal);
            delete expense.taxAmount;
          }
        }
        setExpenses(updatedExpenses);
      }
      return { ...prev, [key]: current === '$' ? 'RS' : '$' };
    });
  };

  // Editable table logic (single input for amount)
  const handleAmountChange = (dayIndex, expenseIndex, newAmount) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
      const expense = updatedExpenses[dayIndex].expenses[expenseIndex];
      const key = `${dayIndex}-${expenseIndex}`;
      const selectedCurrency = currencyMap[key] || '$';
      let amount = parseFloat(newAmount) || 0;
      let amountInUSD = amount;
      
      // Convert to USD if in RS
      if (selectedCurrency === 'RS') {
        amountInUSD = amount / usdRate;
      }
      
      const taxPercentage = getTaxPercentage();
      if (expense.taxIncluded) {
        // amount is total, calculate base and tax
        const baseAmount = amountInUSD / (1 + taxPercentage / 100);
        expense.baseAmount = baseAmount;
        
        if (selectedCurrency === '$') {
          expense.amount = amountInUSD;
        } else {
          // For RS, store the USD amount in amount property
          expense.amount = amountInUSD;
          // And store the RS amount in enteredRS
          expense.enteredRS = amount;
        }
        
        expense.taxAmount = (amountInUSD - baseAmount).toFixed(2);
      } else {
        // amount is base, calculate total
        expense.baseAmount = amountInUSD;
        
        if (selectedCurrency === '$') {
          expense.amount = amountInUSD;
        } else {
          // For RS, store the USD amount in amount property
          expense.amount = amountInUSD;
          // And store the RS amount in enteredRS
          expense.enteredRS = amount;
        }
        
        delete expense.taxAmount;
      }
      
      setExpenses(updatedExpenses);
    }
  };

  // Handle tax checkbox toggle (convert value in-place)
  const handleTaxToggle = (dayIndex, expenseIndex) => {
  const updatedExpenses = [...expenses];
  if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
    const expense = updatedExpenses[dayIndex].expenses[expenseIndex];
    const key = `${dayIndex}-${expenseIndex}`;
    const selectedCurrency = currencyMap[key] || '$';
    const taxPercentage = getTaxPercentage();
    let value = parseFloat(expense.amount || 0);
    if (!expense.taxIncluded) {
      // Going from base (no tax) to tax included: multiply
      const total = value * (1 + taxPercentage / 100);
      expense.amount = Number(total.toFixed(2));
      expense.baseAmount = value;
      expense.taxAmount = (total - value).toFixed(2);
      expense.taxIncluded = true;
      if (selectedCurrency === 'RS') {
        expense.enteredRS = (expense.amount * usdRate).toFixed(2);
      }
    } else {
      // Going from tax included to base (no tax): divide
      const base = value / (1 + taxPercentage / 100);
      expense.amount = Number(base.toFixed(2));
      expense.baseAmount = base;
      delete expense.taxAmount;
      expense.taxIncluded = false;
      if (selectedCurrency === 'RS') {
        expense.enteredRS = (expense.amount * usdRate).toFixed(2);
      }
    }
    setExpenses([...updatedExpenses]);
  }
};



  const handleTypeChange = (dayIndex, expenseIndex, newType) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
      updatedExpenses[dayIndex].expenses[expenseIndex].type = newType;
      setExpenses(updatedExpenses);
    }
  };

  const addExpense = (dayIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex]) {
      updatedExpenses[dayIndex].expenses.push({ type: "", amount: 0 });
      setExpenses(updatedExpenses);
    }
  };

  const removeExpense = (dayIndex, expenseIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses.length > 1) {
      updatedExpenses[dayIndex].expenses.splice(expenseIndex, 1);
      setExpenses(updatedExpenses);
    }
  };

  // Track which input cell is being edited: { day: index, expense: index } | null
  const [editingCell, setEditingCell] = useState(null);

  // Helper: handle Enter key to blur input
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Save changes to API (similar to OtherExpensesPopup)
  const saveChanges = async () => {
    if (!token || !itineraryId) {
      onClose();
      return;
    }
    setUpdating(true);
    setError(null);
    try {
      // Get the full itinerary data from the API response to preserve other data
      const itineraryData = apiResponseData?.data?.[0] || apiResponseData || {};
      // Create a deep copy to avoid mutating the original
      const updatedItineraryData = JSON.parse(JSON.stringify(itineraryData));
      // Make sure itinerary_cost array exists
      if (!updatedItineraryData.itinerary_cost) {
        updatedItineraryData.itinerary_cost = [];
      }
      // Patch miscellaneous_expenses for each day
      expenses.forEach((dayExpense, dayIndex) => {
        const dayNumber = dayExpense.day;
        const dayString = dayNumber.startsWith('Day ') ? dayNumber : `Day ${dayNumber.toString().padStart(2, '0')}`;
        // Find or create the day entry
        let dayCostIndex = updatedItineraryData.itinerary_cost.findIndex(cost => cost.day === dayString);
        if (dayCostIndex === -1) {
          updatedItineraryData.itinerary_cost.push({
            day: dayString,
            calendar_date: dayExpense.date || "Not Selected",
            miscellaneous_expenses: [],
            day_total_cost: "0.00",
            day_total_cost_with_tax: "0.00"
          });
          dayCostIndex = updatedItineraryData.itinerary_cost.length - 1;
        }
        // Update miscellaneous_expenses
        updatedItineraryData.itinerary_cost[dayCostIndex].miscellaneous_expenses = dayExpense.expenses.map((expense, expenseIndex) => {
          const currency = currencyMap[`${dayIndex}-${expenseIndex}`] || '$';
          let final_usd_amount = 0;
          if (currency === 'RS') {
            // Use enteredRS if available, otherwise fallback to amount * usdRate
            const rsVal = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) : (parseFloat(expense.amount) * usdRate);
            final_usd_amount = isNaN(rsVal) ? 0 : rsVal / usdRate;
          } else {
            final_usd_amount = parseFloat(expense.amount) || 0;
          }
          return {
            type: expense.type,
            amount: parseFloat(expense.amount) || 0,
            taxIncluded: !!expense.taxIncluded,
            currency,
            final_usd_amount: parseFloat(final_usd_amount.toFixed(2)), // always USD, for backend math
            manually_updated: true // Mark this expense as manually updated
          };
        });
        // Print the JSON for this day's expenses for debug
        console.log('MiscellaneousExpensesPopup: Saving miscellaneous_expenses for day', dayExpense.day, JSON.stringify(updatedItineraryData.itinerary_cost[dayCostIndex].miscellaneous_expenses, null, 2));
        // Calculate and update the day's total miscellaneous expenses using the sum of final_usd_amount
        const dayTotal = updatedItineraryData.itinerary_cost[dayCostIndex].miscellaneous_expenses.reduce((sum, expense) => sum + (parseFloat(expense.final_usd_amount) || 0), 0);
        updatedItineraryData.itinerary_cost[dayCostIndex].miscellaneous_expenses_total = dayTotal.toFixed(2);
        // Add miscellaneous_expenses_total to day_total_cost
        const prevDayTotal = parseFloat(updatedItineraryData.itinerary_cost[dayCostIndex].day_total_cost) || 0;
        updatedItineraryData.itinerary_cost[dayCostIndex].day_total_cost = (prevDayTotal + dayTotal).toFixed(2);
      });
      // Prepare payload
      const apiPayload = {
        itinerary_id: itineraryId,
        updates: updatedItineraryData,
        price_change: true
      };
      // Print the request body to console
      console.log("[MiscellaneousExpensesPopup] update_itinerary_data body:", JSON.stringify(apiPayload, null, 2));
      // Send update request
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/update_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update miscellaneous expenses: ${response.status} - ${errorData.error || "Unknown error"}`);
      }
      // Optionally show a success message
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const getTotal = () => {
  let total = 0;
  expenses.forEach((day, dayIndex) => {
    day.expenses.forEach((expense, expenseIndex) => {
      const key = `${dayIndex}-${expenseIndex}`;
      const selectedCurrency = currencyMap[key] || '$';
      let amount = 0;
      if (selectedCurrency === 'RS') {
        // Use enteredRS if available, otherwise convert from USD
        amount = expense.enteredRS !== undefined && expense.enteredRS !== null && expense.enteredRS !== ''
          ? parseFloat(expense.enteredRS)
          : (parseFloat(expense.amount) * usdRate);
        amount = amount / usdRate;
      } else {
        amount = parseFloat(expense.amount) || 0;
      }
      if (isFinite(amount)) {
        total += amount;
      }
    });
  });
  return total;
};




  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Loading Miscellaneous Expenses Data...</p>
        </div>
      </div>
    );
  }
  if (updating) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Updating Miscellaneous Expenses Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out">
      <div
  className="bg-white border border-blue-200 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] relative transform transition-all duration-300 ease-in-out overflow-hidden flex flex-col"
  style={{
    background: 'linear-gradient(to bottom right, #ffffff, #f7fbff)',
    boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5), 0 8px 10px -6px rgba(59, 130, 246, 0.3)'
  }}
>
        {/* Close button */}
        <button
  onClick={onClose}
  className="absolute top-2 right-2 text-blue-500 hover:text-blue-700 transition-colors z-10"
  aria-label="Close"
>
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
</button>
        <div className="p-4 border-b border-blue-200 sticky top-0 bg-white z-10 shadow-sm">
  <h2 className="text-blue-800 text-center font-bold text-xl uppercase tracking-wider">Miscellaneous Expenses</h2>
</div>
<div className="flex-1 overflow-hidden p-4">
        {loading ? (
  <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
    <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
      <p className="text-blue-800 text-center">Loading Miscellaneous Expenses Data...</p>
    </div>
  </div>
        ) : error ? (
          <div className="bg-red-100 text-red-700 p-2 rounded mb-4 text-center border border-red-200">{error}</div>
        ) : (
          <div className="overflow-auto max-h-[calc(90vh-180px)] rounded-lg shadow-inner">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-blue-100">
  <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0 w-20">Day</th>
  <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">Expenses</th>
  <th className="border border-blue-200 p-3 text-blue-700 text-right font-medium text-xs uppercase tracking-wider sticky top-0 w-28">Amount</th>
</tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {expenses.map((day, dayIndex) => (
                  <React.Fragment key={`day-${dayIndex}`}>
                    {day.expenses.map((expense, expenseIndex) => (
                      <tr key={`expense-${dayIndex}-${expenseIndex}`} className={`${expenseIndex % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-blue-100 transition-colors`}>
                        {/* Only show day info on the first expense row for each day */}
                        {expenseIndex === 0 ? (
                          <td className="px-4 py-3 text-gray-800 align-top" rowSpan={day.expenses.length}>
                            <div className="font-medium">{day.day}</div>
                            <div className="text-xs text-gray-500">{day.date}</div>
                          </td>
                        ) : null}
                        <td className="px-4 py-3 text-gray-800">
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={expense.type || ''}
                              onChange={(e) => handleTypeChange(dayIndex, expenseIndex, e.target.value)}
                              className="flex-grow px-2 py-1 border border-blue-200 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
                              placeholder="Expense type"
                            />
                            <button
                              onClick={() => removeExpense(dayIndex, expenseIndex)}
                              className="text-red-500 hover:text-red-700 focus:outline-none"
                              title="Remove expense"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          <div className="flex items-center justify-end space-x-2">
                            {/* Toggle switch for currency */}
                            <button
                              type="button"
                              onClick={() => handleCurrencyToggle(dayIndex, expenseIndex)}
                              className={`relative w-12 h-6 flex items-center rounded-full focus:outline-none transition border border-blue-300 ${((currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$') ? 'bg-blue-200' : 'bg-green-200'}`}
                              title="Toggle currency"
                              style={{ minWidth: 48 }}
                            >
                              <span className={`absolute left-1 text-xs font-bold transition-colors ${(currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$' ? 'text-blue-900' : 'text-gray-400'}`}>$</span>
                              <span className={`absolute right-1 text-xs font-bold transition-colors ${(currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === 'RS' ? 'text-green-900' : 'text-gray-400'}`}>RS</span>
                              <span
                                className={`absolute transition-transform duration-200 ease-in-out bg-white h-5 w-5 rounded-full shadow ${((currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$') ? 'translate-x-0' : 'translate-x-6'}`}
                                style={{ left: 0, top: 0.5 }}
                              ></span>
                            </button>
                            <div className="flex flex-col items-end">
  <input
    type="number"
    min="0"
    step="0.01"
    value={(() => {
      const isRS = (currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === 'RS';
      let val;
      if (isRS) {
        // For RS currency, show the enteredRS value or convert from USD
        val = expenses[dayIndex].expenses[expenseIndex].enteredRS !== undefined
          ? expenses[dayIndex].expenses[expenseIndex].enteredRS
          : (parseFloat(expenses[dayIndex].expenses[expenseIndex].amount || 0) * usdRate).toFixed(2);
      } else {
        // For USD, show the amount directly
        val = expenses[dayIndex].expenses[expenseIndex].amount || '';
      }
      if (val === null || val === undefined || val === '' || isNaN(val)) return '';
      return String(val);
    })() }
    onChange={(e) => handleAmountChange(dayIndex, expenseIndex, e.target.value)}
    onFocus={() => setEditingCell({ day: dayIndex, expense: expenseIndex })}
    onBlur={() => setEditingCell(editingCell => (editingCell && editingCell.day === dayIndex && editingCell.expense === expenseIndex) ? null : editingCell)}
    onKeyDown={handleInputKeyDown}
    className="w-20 text-right border border-blue-200 rounded p-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
  />
  {/* Display conversion under the input, only if not editing this cell */}
  {(!editingCell || editingCell.day !== dayIndex || editingCell.expense !== expenseIndex) && (() => {
    const isRS = (currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === 'RS';
    const expense = expenses[dayIndex].expenses[expenseIndex];
    
    // Get the correct RS value
    const rsVal = isRS && expense.enteredRS !== undefined
      ? parseFloat(expense.enteredRS) || 0
      : parseFloat(expense.amount || 0) * usdRate;
    
    // Get the correct USD value
    const usdVal = isRS
      ? parseFloat(expense.amount || 0)
      : parseFloat(expense.amount || 0);
    
    const rsDisplay = rsVal.toFixed(2);
    const usdDisplay = usdVal.toFixed(2);
    
    if (isRS) {
      return (
        <div className="text-xs text-gray-500 mt-1 block text-right" style={{lineHeight:1}}>
          <div>$ {usdDisplay} = Rs {rsDisplay} / {usdRate}</div>
        </div>
      );
    } else {
      return (
        <div className="text-xs text-gray-500 mt-1 block text-right" style={{lineHeight:1}}>
          <div>Rs {rsDisplay} = $ {usdDisplay} * {usdRate}</div>
        </div>
      );
    }
  })() }
</div>
                            {/* Show selected currency symbol to the right */}
                            <span className="ml-1 font-semibold text-xs text-gray-700">{currencyMap[`${dayIndex}-${expenseIndex}`] || '$'}</span>
                            <label className="flex items-center ml-2 text-xs text-blue-700">
                              <input
                                type="checkbox"
                                checked={!!expense.taxIncluded}
                                onChange={() => handleTaxToggle(dayIndex, expenseIndex)}
                                className="h-4 w-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500 ml-1"
                              />
                              <span className="ml-1">Tax Included</span>
                            </label>
                            {/* Show tax calculation only if not editing this cell */}
                            {expense.taxIncluded && getTaxPercentage() > 0 && (!editingCell || editingCell.day !== dayIndex || editingCell.expense !== expenseIndex) && (
                              <span className="ml-2 text-xs text-blue-600">
                                (incl. {getTaxPercentage()}% tax, {expense.taxAmount !== undefined ? expense.taxAmount : ((parseFloat(expense.amount || 0) - (parseFloat(expense.amount || 0) / (1 + getTaxPercentage()/100))).toFixed(2))})
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Add expense button for each day */}
                    <tr className="bg-gray-50">
                      <td colSpan="3" className="px-4 py-2">
                        <button
                          onClick={() => addExpense(dayIndex)}
                          className="w-full flex items-center justify-center px-3 py-1 border border-blue-300 text-blue-600 text-sm rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add Expense
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
                <tr className="bg-blue-100 sticky bottom-0 shadow-md">
                  <td colSpan="2" className="border border-blue-200 p-3 text-blue-800 text-center font-semibold">
  Total
</td>
<td className="border border-blue-200 p-3 text-blue-800 text-right font-semibold">
  $ {formatCurrency(getTotal())}
</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        </div>
       
        {/* Save and Cancel buttons - fixed at bottom */}
        <div className="p-4 border-t border-blue-200 flex justify-end space-x-3 bg-white sticky bottom-0 shadow-md z-10">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            onClick={saveChanges}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiscellaneousExpensesPopup;
